import { computed, watch, type Ref } from "vue";
import { storeToRefs } from "pinia";
import { useJcdCampaignsStore } from "@stores/jcdCampaigns";
import { createFilters } from "@/utils/composables/filters/createFilters";
import {
  useFiltersUrlSync,
  arrayParam,
  stringParam,
  type UrlFiltersState,
} from "@/utils/composables/filters/useFiltersUrlSync";

const urlParams = {
  year: arrayParam(),
  week: arrayParam(),
  family: arrayParam(),
  family_class: arrayParam(),
  support: arrayParam(),
  networks: arrayParam(),
  formats: arrayParam(),
  level: arrayParam(),
  search: stringParam(),
};

type JcdCampaignsUrlState = UrlFiltersState<typeof urlParams>;

const asOptions = (values: string[] | number[]) =>
  values.map((value) => ({ label: String(value), value: String(value) }));

export const useJcdCampaignsFilters = () => {
  const store = useJcdCampaignsStore();
  const {
    searchQuery,
    yearFilter,
    weekFilter,
    familyFilter,
    familyClassFilter,
    supportFilter,
    networksFilter,
    formatsFilter,
    levelFilter,
    referentials,
  } = storeToRefs(store);

  const filterMap: Record<string, Ref<string[]>> = {
    year: yearFilter,
    week: weekFilter,
    family: familyFilter,
    family_class: familyClassFilter,
    support: supportFilter,
    networks: networksFilter,
    formats: formatsFilter,
    level: levelFilter,
  };

  const familyClassOptions = computed(() => {
    const families = referentials.value?.families || {};
    const scope = familyFilter.value.length ? familyFilter.value : Object.keys(families);
    return asOptions([...new Set(scope.flatMap((family) => families[family] || []))].sort());
  });

  const serializeUrlState = () => {
    const filters: Record<string, string[]> = {};
    Object.entries(filterMap).forEach(([key, filterRef]) => {
      if (filterRef.value.length) filters[key] = filterRef.value;
    });
    return { ...filters, search: searchQuery.value };
  };

  const applyUrlState = (state: JcdCampaignsUrlState) => {
    const { search, ...filters } = state;
    Object.entries(filterMap).forEach(([key, filterRef]) => {
      filterRef.value = filters[key as keyof typeof filters] || [];
    });
    store.setSearchQuery(search);
  };

  const urlSync = useFiltersUrlSync({
    params: urlParams,
    serialize: serializeUrlState,
    apply: applyUrlState,
    onExternalChange: (state) => {
      applyUrlState(state);
      store.fetch(1);
    },
  });

  urlSync.restoreFromUrl();
  watch(searchQuery, () => urlSync.syncUrl());

  return createFilters({
    filters: [
      {
        id: "year",
        label: "Année",
        options: () => asOptions(Object.keys(referentials.value?.weeks_by_year || {})),
        model: yearFilter,
      },
      {
        id: "week",
        label: "Semaine",
        hasSearch: true,
        options: Array.from({ length: 53 }, (_, i) => ({ label: `S${i + 1}`, value: String(i + 1) })),
        model: weekFilter,
      },
      {
        id: "family",
        label: "Famille",
        hasSearch: true,
        options: () => asOptions(Object.keys(referentials.value?.families || {})),
        model: familyFilter,
      },
      {
        id: "family_class",
        label: "Classe",
        hasSearch: true,
        options: familyClassOptions,
        model: familyClassFilter,
      },
      {
        id: "support",
        label: "Support",
        options: () => asOptions(referentials.value?.supports || []),
        model: supportFilter,
      },
      {
        id: "networks",
        label: "Réseau",
        hasSearch: true,
        options: () => asOptions(referentials.value?.networks || []),
        model: networksFilter,
      },
      {
        id: "formats",
        label: "Format",
        options: () => asOptions(referentials.value?.formats || []),
        model: formatsFilter,
      },
      {
        id: "level",
        label: "Niveau",
        options: () => asOptions(referentials.value?.levels || []),
        model: levelFilter,
      },
    ],
    search: async () => {
      urlSync.syncUrl();
      await store.fetch(1);
    },
    clear: async () => {
      await store.clearFilters();
      urlSync.syncUrl();
    },
  });
};
